import { useMemo, useState } from "react"

import { Database, Plus } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  CreateDataConnectionInput,
  DataConnection,
  UpdateDataConnectionInput,
} from "@/api/data-connections-api"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"

import { AddDataConnectionDialog } from "./add-data-connection-dialog"
import { AdminDashboardError } from "./admin-dashboard-error"
import { LoadingCard } from "./admin-dashboard-loading"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { ConnectionSchemaSheet } from "./connection-schema-sheet"
import { DataConnectionShareDialog } from "./data-connection-share-dialog"
import { DataConnectionsTable } from "./data-connections-table"
import {
  useCreateDataConnectionMutation,
  useDataConnectionsQuery,
  useDeleteDataConnectionMutation,
  useIntrospectDataConnectionMutation,
  useTestDataConnectionMutation,
  useUpdateDataConnectionMutation,
} from "../hooks/use-data-connections"

export function DataConnectionsManagementCard() {
  const { t } = useTranslation()

  const { data: connections, isLoading, error, refetch } = useDataConnectionsQuery()
  const createMutation = useCreateDataConnectionMutation()
  const updateMutation = useUpdateDataConnectionMutation()
  const deleteMutation = useDeleteDataConnectionMutation()
  const testMutation = useTestDataConnectionMutation()
  const introspectMutation = useIntrospectDataConnectionMutation()

  const handleIntrospect = async (connection: DataConnection) => {
    try {
      await introspectMutation.mutateAsync(connection.id)
      toast.success(
        t(
          "adminDashboard.dataConnections.toast.introspectStarted",
          "Schema scan started — catalog will update shortly"
        )
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t(
              "adminDashboard.dataConnections.toast.introspectFailed",
              "Failed to start schema scan"
            )
      )
    }
  }

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<DataConnection | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DataConnection | null>(null)
  const [schemaTarget, setSchemaTarget] = useState<DataConnection | null>(null)
  const [shareTarget, setShareTarget] = useState<DataConnection | null>(null)

  const updatingId = useMemo(() => {
    if (updateMutation.isPending) return updateMutation.variables.id
    if (deleteMutation.isPending && typeof deleteMutation.variables === "string") {
      return deleteMutation.variables
    }
    return null
  }, [updateMutation, deleteMutation])

  const openCreate = () => {
    setEditTarget(null)
    setDialogOpen(true)
  }
  const openEdit = (connection: DataConnection) => {
    setEditTarget(connection)
    setDialogOpen(true)
  }

  const handleSubmit = async (payload: CreateDataConnectionInput | UpdateDataConnectionInput) => {
    try {
      if (editTarget) {
        await updateMutation.mutateAsync({
          id: editTarget.id,
          input: payload as UpdateDataConnectionInput,
        })
        toast.success(t("adminDashboard.dataConnections.toast.updated", "Connection updated"))
      } else {
        await createMutation.mutateAsync(payload as CreateDataConnectionInput)
        toast.success(t("adminDashboard.dataConnections.toast.created", "Connection added"))
      }
      setDialogOpen(false)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("adminDashboard.dataConnections.toast.saveFailed", "Failed to save connection")
      toast.error(message)
    }
  }

  const handleToggle = async (connection: DataConnection, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ id: connection.id, input: { enabled } })
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t(
              "adminDashboard.dataConnections.toast.toggleFailed",
              "Failed to update connection status"
            )
      toast.error(message)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success(t("adminDashboard.dataConnections.toast.deleted", "Connection removed"))
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("adminDashboard.dataConnections.toast.deleteFailed", "Failed to delete connection")
      toast.error(message)
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <CollapsibleManagementSection
      icon={<Database className="size-5" />}
      id="data-connections"
      storageKey="admin-management-data-connections-expanded"
      title={t("adminDashboard.dataConnections.title", "Data connections")}
      headerActions={
        <Button disabled={isLoading} size="sm" onClick={openCreate}>
          <Plus className="mr-2 size-4" />
          {t("adminDashboard.dataConnections.add", "Add connection")}
        </Button>
      }
    >
      {isLoading && (
        <LoadingCard title={t("adminDashboard.dataConnections.title", "Data connections")} />
      )}
      {!isLoading && error && <AdminDashboardError error={error} onRetry={() => void refetch()} />}
      {!isLoading && !error && (
        <DataConnectionsTable
          connections={connections ?? []}
          disableAll={createMutation.isPending}
          updatingId={updatingId}
          onDelete={setDeleteTarget}
          onEdit={openEdit}
          onIntrospect={handleIntrospect}
          onShare={setShareTarget}
          onShowSchema={setSchemaTarget}
          onTest={(conn) => testMutation.mutateAsync(conn.id)}
          onToggleStatus={handleToggle}
        />
      )}

      <DataConnectionShareDialog
        connection={shareTarget ?? undefined}
        open={shareTarget !== null}
        onOpenChange={(open) => {
          if (!open) setShareTarget(null)
        }}
      />

      <AddDataConnectionDialog
        existing={editTarget}
        isOpen={dialogOpen}
        isPending={createMutation.isPending || updateMutation.isPending}
        onOpenChange={setDialogOpen}
        onSubmit={handleSubmit}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("adminDashboard.dataConnections.deleteDialog.title", "Delete connection?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "adminDashboard.dataConnections.deleteDialog.description",
                "This removes the connection config and soft-deletes its Key Vault secret. Dashboards that rely on it will stop refreshing."
              )}
              {deleteTarget && (
                <span className="mt-2 block font-mono text-sm">{deleteTarget.name}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()}>
              {t("adminDashboard.dataConnections.deleteDialog.confirm", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConnectionSchemaSheet
        connection={schemaTarget}
        onClose={() => setSchemaTarget(null)}
        onRescan={handleIntrospect}
      />
    </CollapsibleManagementSection>
  )
}
